import { useGo, useList, useTranslate } from "@refinedev/core";
import { useState } from "react";
import { Button, DatePicker, Flex, TimeRangePickerProps, List } from "antd";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import VirtualList from "rc-virtual-list";
import { MediaControl } from "../types";
import { ArrowRightOutlined } from "@ant-design/icons";
import { sortByDate } from "helpers/utils";
import { Uuid } from "components/UUID";
import localizedFormat from "dayjs/plugin/localizedFormat";
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;

export const MediaAlertList = () => {
  const go = useGo();
  const t = useTranslate();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().add(-1, "d"),
    dayjs(),
  ]);
  const { data, isFetching, error } = useList<MediaControl>({
    resource: `media/media/alerts?from_time=${dateRange?.[0]?.format("YYYY-MM-DD HH:mm:ss")}&to_time=${dateRange?.[1]?.format("YYYY-MM-DD HH:mm:ss")}`,
    queryOptions: {
      enabled: dateRange !== null,
    },
  });

  const handleDateRangeChange = (values: typeof dateRange) => {
    setDateRange(values);
  };

  const gotoMedia = (id: string) => {
    go({ to: { resource: "media_media", action: "show", id } });
  };

  const sortedData = sortByDate<MediaControl, "updated">(
    data?.data,
    "updated",
    "DESC"
  ).map((x, idx) => ({
    ...x,
    id: idx, // fake unique id to avoid key warnings in List
  }));

  const rangePresets: TimeRangePickerProps["presets"] = [
    {
      label: t("media.components.MediaAlertList.last24H"),
      value: [dayjs().add(-1, "d"), dayjs()],
    },
    {
      label: t("media.components.MediaAlertList.last7Days"),
      value: [dayjs().add(-7, "d"), dayjs()],
    },
    {
      label: t("media.components.MediaAlertList.last14Days"),
      value: [dayjs().add(-14, "d"), dayjs()],
    },
    {
      label: t("media.components.MediaAlertList.last30Days"),
      value: [dayjs().add(-30, "d"), dayjs()],
    },
  ];

  return (
    <Flex vertical gap={10}>
      <RangePicker
        size="middle"
        showTime
        defaultValue={dateRange}
        format="YYYY/MM/DD HH:mm:ss"
        presets={rangePresets}
        onChange={handleDateRangeChange}
      />

      <List size="small" loading={isFetching}>
        <VirtualList data={sortedData} height={450} itemKey={"id"}>
          {(item: MediaControl, index) => (
            <List.Item
              style={{ cursor: "pointer" }}
              onClick={() => gotoMedia(item.media_id)}
              key={index}
              actions={[
                <Button key={1} icon={<ArrowRightOutlined />} type="text" />,
              ]}
            >
              <List.Item.Meta
                title={`${t("media.components.MediaAlertList.newsGeneratedStart")} ${item.control.split(" ")[1]} ${t("media.components.MediaAlertList.newsGeneratedEnd")}`}
                description={
                  <>
                    {dayjs(item.updated).format("LLLL")} •{" "}
                    {dayjs(item.updated).fromNow()}
                  </>
                }
              />
              <Uuid id={item.media_id} />
            </List.Item>
          )}
        </VirtualList>
      </List>
    </Flex>
  );
};
